import { useState } from 'react';
import PublicPageLayout from '@/components/feature/PublicPageLayout';

const faqs = [
  {
    question: 'O que é o WZap PRO?',
    answer: 'O WZap PRO é uma plataforma SaaS completa de marketing via WhatsApp Business. Permite criar campanhas de disparo em massa, gerenciar contatos, criar templates personalizados com variáveis dinâmicas, acompanhar métricas em tempo real e automatizar follow-ups — tudo com a segurança da API oficial do Meta ou da nossa infraestrutura gerenciada.',
  },
  {
    question: 'Preciso ter API oficial do WhatsApp Business para usar?',
    answer: 'Não é obrigatório. Oferecemos duas formas de conexão: você pode conectar sua própria API oficial do Meta (WhatsApp Business Cloud API) ou usar nossa infraestrutura gerenciada via Evolution API, pronta para uso em minutos. Você escolhe a melhor opção para o seu negócio.',
  },
  {
    question: 'Quais planos estão disponíveis?',
    answer: 'Oferecemos quatro planos: Starter (R$ 97/mês) com 5.000 mensagens/mês e 1 usuário; Professional (R$ 297/mês) com 50.000 mensagens/mês e até 5 usuários; Business (R$ 597/mês) com 150.000 mensagens/mês e até 10 usuários; e Enterprise sob consulta, com limites personalizados, white label e servidor dedicado.',
  },
  {
    question: 'Como funciona o disparo em massa?',
    answer: 'É simples: importe sua base de contatos via CSV ou conecte seu CRM, crie um template de mensagem com variáveis dinâmicas (nome, empresa, etc.), selecione o grupo de destino e clique em enviar. A plataforma processa a fila automaticamente, respeitando os limites da API e entregando relatórios em tempo real sobre entrega, leitura e resposta.',
  },
  {
    question: 'Posso usar minha própria infraestrutura?',
    answer: 'Sim. Se você já possui uma conta de API oficial do WhatsApp Business Cloud, pode conectá-la diretamente à WZap PRO e usar nossa plataforma como gerenciadora de campanhas. Alternativamente, você pode usar nossa infraestrutura pronta via Evolution API, sem necessidade de configurar nada do lado do Meta.',
  },
  {
    question: 'Meus dados estão seguros?',
    answer: 'Absolutamente. Utilizamos criptografia TLS/SSL para todos os dados em trânsito, criptografia de dados sensíveis em repouso, autenticação multifator (2FA), controle de acesso baseado em função (RBAC) e monitoramento contínuo de segurança. Estamos em conformidade com a LGPD (Lei nº 13.709/2018).',
  },
  {
    question: 'É necessário opt-in dos contatos?',
    answer: 'Sim. Conforme a LGPD e as diretrizes da Meta para uso do WhatsApp Business API, você deve possuir consentimento explícito (opt-in) de todos os contatos antes de enviar campanhas. A WZap PRO exige essa confirmação no momento da importação de contatos e reserva-se o direito de suspender contas que enviem spam.',
  },
  {
    question: 'Qual a taxa de entrega das mensagens?',
    answer: 'Com a API oficial do Meta (WhatsApp Business Cloud), a taxa média de entrega é de 98,5%. Com a Evolution API, a taxa varia conforme a qualidade do número de telefone, estado da conta e frequência de envio. Recomendamos sempre usar números verificados e manter uma frequência de envio saudável.',
  },
  {
    question: 'Como funciona o suporte técnico?',
    answer: 'Oferecemos suporte técnico via chat na plataforma, email e WhatsApp. Usuários dos planos Professional e superiores têm acesso a suporte prioritário com tempo de resposta garantido. Nossa equipe está disponível para ajudar com setup, integrações, dúvidas de uso e troubleshooting.',
  },
  {
    question: 'Posso cancelar a assinatura a qualquer momento?',
    answer: 'Sim. Você pode cancelar sua assinatura a qualquer momento diretamente nas configurações da sua conta. O acesso à plataforma permanece ativo até o final do período pago. Oferecemos garantia de reembolso de 7 dias para novas assinaturas.',
  },
  {
    question: 'Aceita pagamento via PIX?',
    answer: 'Sim! Aceitamos pagamentos via PIX e cartão de crédito através do MercadoPago, e também cartão de crédito via Stripe. As cobranças são recorrentes mensais e você pode alterar a forma de pagamento a qualquer momento nas configurações da conta.',
  },
  {
    question: 'Quanto tempo leva para começar a enviar campanhas?',
    answer: 'Menos de 5 minutos. Após criar sua conta e conectar seu WhatsApp (seja via API Meta ou Evolution API), você já pode importar contatos, criar um template e enviar sua primeira campanha. O setup é rápido e guiado passo a passo dentro da plataforma.',
  },
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <PublicPageLayout
      title="FAQ - Perguntas Frequentes | WZap PRO"
      description="Encontre respostas para as dúvidas mais comuns sobre a plataforma WZap PRO: planos, disparo em massa, segurança, API do WhatsApp e muito mais."
      schemaJsonLd={schemaJsonLd}
    >
      <article>
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-3">
              Perguntas Frequentes
            </h2>
            <p className="text-foreground-600 dark:text-foreground-400 leading-relaxed">
              Tire suas dúvidas sobre a plataforma WZap PRO. Se não encontrar o que procura,
              {' '}
              <a href="mailto:nx3designer@gmail.com" className="text-primary-600 hover:text-primary-700 underline">entre em contato conosco</a>.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className={`rounded-xl border transition-all duration-200 ${
                    isOpen
                      ? 'border-primary-200 dark:border-primary-500/30 bg-primary-50/40 dark:bg-primary-500/5'
                      : 'border-background-200 dark:border-background-300 bg-background-50 dark:bg-background-100/50'
                  }`}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 p-4 md:p-5 text-left cursor-pointer"
                  >
                    <span className="font-heading font-semibold text-foreground-950 dark:text-foreground-100 text-sm md:text-base">
                      {faq.question}
                    </span>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      isOpen
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-200 dark:bg-background-300 text-foreground-500'
                    }`}>
                      <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm md:text-base text-foreground-600 dark:text-foreground-400 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 p-5 bg-background-50 dark:bg-background-200/40 rounded-xl border border-background-200 dark:border-background-300">
            <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
              <strong className="text-foreground-800 dark:text-foreground-200">Ainda tem dúvidas?</strong>
              {' '}Nosso time de suporte está pronto para ajudar. Envie um email para{' '}
              <a href="mailto:nx3designer@gmail.com" className="text-primary-600 hover:text-primary-700 underline">nx3designer@gmail.com</a>
              {' '}ou fale pelo WhatsApp{' '}
              <a href="https://wa.me/5554996542326" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">+55 54 99654-2326</a>.
            </p>
          </div>
        </div>
      </article>
    </PublicPageLayout>
  );
}